import {TFunction} from "i18next";
import {isStringUndefinedOrBlank} from "@ots-frontend/ots-data";

export const validateRequired = (value: unknown, t: TFunction): string | undefined => {
  if (typeof value === 'string') {
    return isStringUndefinedOrBlank(value) ? t<string>("common.validation.required", "Required") : undefined
  }

  return value === null || typeof value === 'undefined'
    ? t<string>("common.validation.required", "Required")
    : undefined
};

export const validateRequiredWhen = (required: boolean, value: unknown, t: TFunction): string | undefined => {
  return required ? validateRequired(value, t) : undefined
};

export const validateLatCoord = (value: number | undefined, required: boolean, t: TFunction): string | undefined => {
  if (required && typeof value === 'undefined') {
    return t<string>("common.validation.required", "Required")
  }

  if (value < -90 || value > 90) {
    return t<string>("common.validation.latitude", "Latitude must be between -90 and 90")
  }

  return undefined;
};

export const validateLongCoord = (value: number | undefined, required: boolean, t: TFunction): string | undefined => {
  if (required && typeof value === 'undefined') {
    return t<string>("common.validation.required", "Required")
  }

  if (value < -180 || value > 180) {
    return t<string>("common.validation.longitude", "Longitude must be between -180 and 180")
  }

  return undefined;
};

export const validateDomain = (value: string | undefined, required: boolean, t: TFunction): string | undefined => {
  if (required && !value || value.length === 0) {
    return t<string>("common.validation.required", "Required")
  }

  const domain = value.toLowerCase();

  if (
    domain.includes("://") ||
    domain.includes("/") ||
    domain.includes("?") ||
    domain.includes("#") ||
    domain.includes(":") ||
    domain.includes("@") ||
    /\s/.test(domain)
  ) {
    return t<string>("common.validation.domain", "Invalid domain");
  }

  // overall length
  if (domain.length > 253) {
    return t<string>("common.validation.domain", "Invalid domain");
  }

  const labels = domain.split(".");

  // require at least one dot: example.com
  if (labels.length < 2) {
    return t<string>("common.validation.domain", "Invalid domain");
  }

  for (const label of labels) {
    // empty label, too long, starts/ends with hyphen
    if (!label || label.length > 63 || label.startsWith("-") || label.endsWith("-")) {
      return t<string>("common.validation.domain", "Invalid domain");
    }

    // letters, digits, hyphen only
    if (!/^[a-z0-9-]+$/.test(label)) {
      return t<string>("common.validation.domain", "Invalid domain");
    }
  }

  const tld = labels[labels.length - 1];

  // TLD must be letters only and at least 2 chars
  if (!/^[a-z]{2,63}$/.test(tld)) {
    return t<string>("common.validation.domain", "Invalid domain");
  }

  return undefined;
}

export const extractDomainFromEmail = (email: string | undefined | null): string | undefined => {
  if (!email) {
    return undefined;
  }

  const trimmed = email.trim().toLowerCase();

  const atIndex = trimmed.lastIndexOf("@");

  // must have exactly one @ and something after it
  if (atIndex <= 0 || atIndex === trimmed.length - 1) {
    return undefined;
  }

  return trimmed.substring(atIndex + 1);
};

export const validateEmailDomain = (
  email: string | undefined | null,
  domain: string | undefined | null,
  allowSubdomains = false,
  t: TFunction
): string | undefined => {
  if (!email || !domain) {
    return t<string>("common.validation.domain.match", "The email and domain must match");
  }

  const normalizedDomain = domain.trim().toLowerCase();

  if (!normalizedDomain) {
    return t<string>("common.validation.domain.match", "The email and domain must match");
  }

  const emailDomain = extractDomainFromEmail(email);

  if (!emailDomain) {
    return t<string>("common.validation.domain.match", "The email and domain must match");
  }

  // exact match
  if (emailDomain === normalizedDomain) {
    return undefined
  }

  // allow subdomains (e.g. mail.example.com matches example.com)
  if (allowSubdomains) {
    if (emailDomain.endsWith("." + normalizedDomain)) {
      return undefined
    }
  }

  return t<string>("common.validation.domain.match", "The email and domain must match");
};
